import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  return (
    <div className="min-h-screen pt-16">
      {/* Organization Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '17c.c',
            url: 'https://17c.c',
            description: '17c.c 是您值得信赖的首选平台，汇聚海量优质内容与资源。',
            foundingDate: '2024',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              email: 'contact@17c.c',
              availableLanguage: ['Chinese'],
            },
          }),
        }}
      />

      {/* Hero */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
          <div className="mt-6 text-center">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
              关于 17c.c
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              17c.c 致力于为用户打造一个高效、专业、值得信赖的内容聚合平台
            </p>
          </div>
        </div>
      </section>

      {/* Brand Intro */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
                品牌故事
              </h2>
              <div className="mt-6 space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  17c.c 的诞生源于一个简单的信念：让优质内容触手可及。在信息爆炸的时代，我们深知用户面对海量信息的困扰——如何快速找到真正有价值的内容？
                </p>
                <p>
                  正是基于这样的思考，17c.c 应运而生。我们汇聚来自不同领域的内容创作者，通过智能推荐算法与人工精选相结合的方式，为用户提供最优质的内容体验。
                </p>
                <p>
                  从上线至今，17c.c 始终坚持以用户需求为核心，不断优化平台功能，拓展内容领域。我们相信，优质内容应该被更多人看到，而17c.c 正是连接内容与读者的最佳桥梁。
                </p>
              </div>
            </div>
            <div className="relative w-full h-64 md:h-80 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20modern%20office%20workspace%20with%20team%20collaboration%2C%20natural%20light%2C%20cream%20and%20coral%20color%20palette%2C%20minimalist%20interior%20design%2C%20creative%20atmosphere%2C%20plants%20and%20wooden%20elements%2C%20professional%20lifestyle%20photography&width=800&height=600&seq=about-brand&orientation=landscape"
                alt="17c.c 团队"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Reputation / Values */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
              我们的承诺
            </h2>
            <p className="mt-3 text-sm text-foreground-500">
              以用户为中心，以品质为根本
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-shield-check-line',
                title: '品质保证',
                desc: '每一篇文章都经过严格筛选与审核，确保内容的准确性、实用性和原创性。',
              },
              {
                icon: 'ri-speed-up-line',
                title: '极致体验',
                desc: '持续优化平台性能，提供流畅的浏览体验，让内容获取更加高效。',
              },
              {
                icon: 'ri-lock-line',
                title: '安全保障',
                desc: '采用行业领先的安全技术，全方位保护用户数据与隐私安全。',
              },
              {
                icon: 'ri-team-line',
                title: '专业团队',
                desc: '汇聚行业资深编辑与技术专家，以专业能力保障平台持续发展。',
              },
              {
                icon: 'ri-lightbulb-flash-line',
                title: '持续创新',
                desc: '紧跟技术趋势，不断探索内容分发的新模式与新体验。',
              },
              {
                icon: 'ri-heart-line',
                title: '用户至上',
                desc: '倾听用户声音，快速响应需求，打造真正服务于用户的内容平台。',
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex flex-col items-center text-center p-6 rounded-lg bg-background-50 border border-background-200/70"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                  <i className={`${item.icon} text-xl`} />
                </div>
                <h3 className="text-base font-semibold text-foreground-900 font-heading">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
              联系我们
            </h2>
            <p className="mt-3 text-sm text-foreground-500">
              我们期待听到您的声音
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
            {[
              {
                icon: 'ri-mail-line',
                title: '电子邮件',
                value: 'contact@17c.c',
              },
              {
                icon: 'ri-customer-service-2-line',
                title: '在线客服',
                value: '工作日 9:00 - 18:00',
              },
              {
                icon: 'ri-map-pin-line',
                title: '办公地址',
                value: '中国 · 互联网产业园区',
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex flex-col items-center text-center p-6 rounded-lg bg-background-100 border border-background-200/70"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                  <i className={`${item.icon} text-xl`} />
                </div>
                <h3 className="text-sm font-semibold text-foreground-900">
                  {item.title}
                </h3>
                <p className="mt-1 text-sm text-foreground-500">
                  {item.value}
                </p>
              </div>
            ))}
          </div>

          {/* Contact Form */}
          <ContactForm />
        </div>
      </section>
    </div>
  );
}

import { useState, type FormEvent } from 'react';

function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeyValue = (formData.get('website_alt') as string || '').trim();

    if (honeyValue) {
      setStatus('success');
      form.reset();
      return;
    }

    try {
      const submitAddr = 'https://readdy.ai/api/form/d9phh79n2trhb35gvfh0';
      const formBody = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          formBody.append(key, value as string);
        }
      });

      const res = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || text || '提交失败，请稍后重试';
        setErrorMsg(msg);
        setStatus('error');
      }
    } catch {
      setErrorMsg('网络异常，请检查网络后重试');
      setStatus('error');
    }
  };

  return (
    <div className="max-w-xl mx-auto bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70">
      <h3 className="text-lg font-semibold text-foreground-900 font-heading text-center mb-6">
        发送消息
      </h3>

      {status === 'success' && (
        <div className="mb-6 p-4 rounded-md bg-accent-100/60 text-accent-700 text-sm text-center">
          消息已成功发送！我们会尽快与您联系。
        </div>
      )}
      {status === 'error' && (
        <div className="mb-6 p-4 rounded-md bg-accent-100/60 text-accent-700 text-sm text-center">
          {errorMsg}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">
            您的姓名 <span className="text-accent-500">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
            placeholder="请输入您的姓名"
          />
        </div>

        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">
            电子邮箱 <span className="text-accent-500">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
            placeholder="请输入您的邮箱"
          />
        </div>

        <div>
          <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1">
            主题
          </label>
          <input
            id="contact-subject"
            name="subject"
            type="text"
            className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
            placeholder="请输入消息主题"
          />
        </div>

        <div>
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">
            消息内容 <span className="text-accent-500">*</span>
          </label>
          <textarea
            id="contact-message"
            name="message"
            required
            rows={4}
            maxLength={500}
            className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
            placeholder="请输入您的消息（最多 500 字）"
          />
        </div>

        {/* Honeypot */}
        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="spam-field"
        />

        <button
          type="submit"
          disabled={status === 'loading'}
          className="w-full py-3 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
        >
          {status === 'loading' ? '发送中...' : '发送消息'}
        </button>
      </form>
    </div>
  );
}